import { NAV_BY_ROLE, isNavActive } from "@/lib/nav-config";
import type { Persona } from "@/lib/persona-types";

export type AnalyticsRole = Persona["role"];
export type AnalyticsConfig = {
  token: string;
  host: string;
  userId: string;
  role: AnalyticsRole;
  replay: boolean;
};

// The sidebar is the feature inventory, including features with no events yet.
export const ANALYTICS_FEATURES = Array.from(new Map(
  Object.values(NAV_BY_ROLE).flat().map(({ href, label }) => [href, { key: href, label }]),
).values());

export function featureForPath(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0];
  return ANALYTICS_FEATURES
    .filter(({ key }) => isNavActive(key, pathname))
    .sort((a, b) => b.key.length - a.key.length)[0]?.key ?? "/other";
}

export function availableFeatures(role: AnalyticsRole): string[] {
  return NAV_BY_ROLE[role].map(({ href }) => href);
}

export type Workflow = "script_submission" | "script_review" | "video_upload" | "video_review";
export type WorkflowEvent = { workflow: Workflow; outcome: "submitted" | "approved" | "edits_requested" | "uploaded" };

// Bounded vocabulary only. Never return the store row, record ID or a filename.
export function workflowForWrite(kind: string, data: unknown): WorkflowEvent | null {
  if (kind === "drive_video" && data !== null && typeof data === "object") {
    return { workflow: "video_upload", outcome: "uploaded" };
  }
  if (kind !== "stage_override" && kind !== "video_stage") return null;
  if (data === "awaiting-am-review" && kind === "stage_override") {
    return { workflow: "script_submission", outcome: "submitted" };
  }
  const workflow = kind === "stage_override" ? "script_review" : "video_review";
  if (data === "approved" || data === "awaiting-brand-review") return { workflow, outcome: "approved" };
  if (data === "edits-requested") return { workflow, outcome: "edits_requested" };
  return null;
}

export function isWorkflowEvent(value: unknown): value is WorkflowEvent {
  if (!value || typeof value !== "object") return false;
  const { workflow, outcome } = value as WorkflowEvent;
  return (workflow === "video_upload" && outcome === "uploaded")
    || (workflow === "script_submission" && outcome === "submitted")
    || ((workflow === "script_review" || workflow === "video_review")
      && (outcome === "approved" || outcome === "edits_requested"));
}
